import { useEffect, useMemo, useState } from "react";
import { Search, Link2, Unlink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";

const words = (s) => (s || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);

export const LinkKit = ({ row, onClose, reload }) => {
  const [kits, setKits] = useState(null);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { http.get("/register/linkable").then(({ data }) => setKits(data)).catch((e) => toast.error(errMsg(e))); }, []);
  const shown = useMemo(() => {
    if (!kits) return [];
    const want = words(row.destination);
    const score = (k) => (k.edition === row.edition ? 2 : 0) + want.filter((w) => (k.destination || "").toLowerCase().includes(w)).length;
    return kits.filter((k) => !q || `${k.title} ${k.destination}`.toLowerCase().includes(q.toLowerCase())).sort((a, b) => score(b) - score(a)).slice(0, 60);
  }, [kits, q, row]);
  const link = async (kit_id) => {
    setBusy(true);
    try { await http.post(`/register/${row.id}/link`, { kit_id }); toast.success(kit_id ? "Kit linked" : "Kit unlinked"); reload(); onClose(); }
    catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="register-link-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">Link a kit to {row.destination}</DialogTitle></DialogHeader>
        <p className="text-sm text-stone-500 -mt-2"><span className="capitalize">{row.edition}</span> · {row.tier || "any tier"} · {row.season || "any season"}. Closest matches are shown first.</p>
        <div className="relative"><Search size={14} className="absolute left-3 top-3 text-stone-400" />
          <input data-testid="register-link-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your kits" className="w-full rounded-sm border border-stone-300 pl-8 pr-3 py-2 text-sm" /></div>
        <div className="max-h-[50vh] overflow-auto divide-y divide-stone-100 border border-stone-200 rounded-sm">
          {!kits && <div className="p-4 text-sm text-stone-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading kits…</div>}
          {kits && !shown.length && <div className="p-4 text-sm text-stone-500">No kits match.</div>}
          {shown.map((k) => (
            <button key={k.id} data-testid={`register-link-kit-${k.id}`} disabled={busy || k.id === row.kit?.id} onClick={() => link(k.id)} className="w-full text-left px-4 py-2.5 hover:bg-stone-50 disabled:opacity-40 flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{k.title}</div>
                <div className="text-xs text-stone-500 truncate"><span className="capitalize">{k.edition}</span> · {k.tier} · {k.season}{k.linked_to ? ` · now linked to ${k.linked_to}` : ""}</div>
              </div>
              <span className={`text-xs ${k.published ? "text-emerald-700" : "text-amber-700"}`}>{k.published ? "Published" : "In progress"}</span>
              <Link2 size={14} className="text-stone-400" />
            </button>
          ))}
        </div>
        {row.kit && <button data-testid="register-unlink" disabled={busy} onClick={() => link(null)} className="self-start inline-flex items-center gap-1.5 text-sm text-red-700 hover:underline"><Unlink size={14} /> Unlink the current kit</button>}
      </DialogContent>
    </Dialog>
  );
};

const EDS = ["luxury", "solo", "wine", "escape", "adventure"];

export const EditionProgress = ({ items, colors }) => (
  <div data-testid="register-progress" className="rounded-xl border border-stone-200 bg-white p-5 mb-6">
    <div className="flex flex-wrap items-center gap-4 mb-4 text-xs text-stone-500">
      <span className="eyebrow">Progress by edition</span>
      <span className="ml-auto inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-emerald-600" /> Published</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-amber-400" /> In progress</span>
      <span className="inline-flex items-center gap-1.5"><i className="w-2.5 h-2.5 rounded-sm bg-stone-200" /> Not built</span>
    </div>
    <div className="space-y-3">
      {EDS.map((e) => {
        const rows = items.filter((r) => r.edition === e);
        if (!rows.length) return null;
        const pub = rows.filter((r) => r.kit?.published).length;
        const prog = rows.filter((r) => r.kit && !r.kit.published).length;
        const pct = (n) => `${(100 * n) / rows.length}%`;
        return (
          <div key={e} data-testid={`register-progress-${e}`} className="grid grid-cols-[110px_1fr_300px] items-center gap-4">
            <span className="text-xs uppercase tracking-wider font-bold" style={{ color: colors[e]?.primary }}>{e}</span>
            <div className="h-3 rounded-full bg-stone-200 overflow-hidden flex" title={`${pub} published · ${prog} in progress · ${rows.length - pub - prog} not built`}>
              <div className="bg-emerald-600 transition-[width] duration-700" style={{ width: pct(pub) }} />
              <div className="bg-amber-400 transition-[width] duration-700" style={{ width: pct(prog) }} />
            </div>
            <span className="text-xs text-stone-600 text-right tabular-nums"><b className="text-emerald-700">{pub}</b> published · <b className="text-amber-700">{prog}</b> in progress · {rows.length - pub - prog} not built</span>
          </div>
        );
      })}
    </div>
  </div>
);
